import { createContext, useContext, useReducer } from "react";
import { buildFieldKey } from "./utils/useSectionEvaluation";

// CaseOfficerEvaluationConext

const ServiceContext = createContext();
const initialState = {
    sectionCCount: 0,
    sectionCFileCount: 0,
    sections: {
        A: { requirements: {}, fields: {}, applicationStatus: null, selfEvaluationId: null, fieldList: [] },
        B: { requirements: {}, fields: {}, applicationStatus: null, selfEvaluationId: null, fieldList: [] },
        // C: { requirements: {}, fields: {}, applicationStatus: null, selfEvaluationId: null, fieldList: [] }
    },
    // global flag that tracks whether a submit was attempted
    submitAttempted: false,
    sectionBMinCriteria: 0
};

function reducer(state, action) {
    switch (action.type) {
        //   SET SELF EVALUATION ID
        case "SET_SELF_EVALUATION_ID": {
            const { section, selfEvaluationId, applicationStatus, isApplicationReturned } = action;

            return {
                ...state,
                sections: {
                    ...state.sections,
                    [section]: {
                        ...state.sections[section],
                        selfEvaluationId,
                        applicationStatus,
                        isApplicationReturned
                    }
                }
            };
        }
        case "SET_SECTION_B_MIN_CRITERIA":
            return { ...state, sectionBMinCriteria: action.value };

        case "SET_SECTION_C_COUNT":
            return { ...state, sectionCCount: action.value };

        case "SET_SECTION_C_FILEUPLOAD":
            return { ...state, sectionCFileCount: action.value };

        case "SET_REQ_LIST": {
            const { section, reqList } = action;
            return {
                ...state,
                sections: {
                    ...state.sections,
                    [section]: {
                        ...state.sections[section],
                        reqList,
                    },
                },
            };
        }

        case "ADD_FILE": {
            const { section, reqId, selfEvalRequirementId, file } = action;
            const existingReq = state.sections[section]?.requirements[reqId] || {
                files: [],
                comment: "",
            };
            return {
                ...state,
                sections: {
                    ...state.sections,
                    [section]: {
                        ...state.sections[section],
                        requirements: {
                            ...state.sections[section]?.requirements,
                            [reqId]: {
                                ...existingReq,
                                files: [
                                    ...(existingReq.files || []),
                                    { ...file, selfEvalRequirementId },
                                ],
                            },
                        },
                    },
                },
            };
        }

        //   FILE DELETE
        case "DELETE_FILE": {
            const { section, reqId, fileId } = action;
            const req = state.sections[section]?.requirements[reqId];

            return {
                ...state,
                sections: {
                    ...state.sections,
                    [section]: {
                        ...state.sections[section],
                        requirements: {
                            ...state.sections[section]?.requirements,
                            [reqId]: {
                                ...req,
                                files: req.files?.filter(f => f.id !== fileId)
                            }
                        }
                    }
                }
            };
        }

        //   REQUIREMENT COMMENT
        case "SET_REQ_COMMENT": {
            const { section, reqId, comment, selfEvalRequirementId, caseOfficerComments, status, peerReviewerComments, } = action;
            return {
                ...state,
                sections: {
                    ...state.sections,
                    [section]: {
                        ...state.sections[section],
                        requirements: {
                            ...state.sections[section]?.requirements,
                            [reqId]: {
                                ...state.sections[section]?.requirements[reqId],
                                comment,
                                requirementId: reqId,
                                selfEvalRequirementId: selfEvalRequirementId,
                                caseOfficerComments: caseOfficerComments,
                                status: status,
                                peerReviewerComments: peerReviewerComments,
                            }
                        }
                    }
                }
            };
        }

        //   FIELD VALUE 
        case "SET_FIELD": {
            const { section, fieldId, value, reqId, selfEvalRequirementId, id, additionalFieldId, brandItemId } = action;
            return {
                ...state,
                sections: {
                    ...state.sections,
                    [section]: {
                        ...state.sections[section],
                        fields: {
                            ...state.sections[section].fields,
                            [fieldId]: {
                                ...state.sections[section].fields[fieldId],
                                value,
                                reqId,
                                selfEvalRequirementId,
                                id,
                                additionalFieldId,
                                brandItemId: brandItemId || null
                            }
                        }
                    }
                }
            };
        }

        //   FIELD COMMENT
        case "SET_FIELD_COMMENT": {
            const { section, fieldId, comment, id, reqId, selfEvalRequirementId, additionalFieldId, brandItemId } = action; // ✅ add reqId
            return {
                ...state,
                sections: {
                    ...state.sections,
                    [section]: {
                        ...state.sections[section],
                        fields: {
                            ...state.sections[section].fields,
                            [fieldId]: {
                                ...state.sections[section].fields[fieldId],
                                comment,
                                id,
                                reqId,
                                selfEvalRequirementId,
                                additionalFieldId,
                                brandItemId: brandItemId || null
                            }
                        }
                    }
                }
            };
        }
        // SET_FIELD_LIST
        case "SET_FIELD_LIST": {
            const { section, fieldList } = action;
            return {
                ...state,
                sections: {
                    ...state.sections,
                    [section]: {
                        ...state.sections[section],
                        fieldList,
                    }
                }
            };
        }
        // marks a field as touched on blur
        case "TOUCH_FIELD": {
            const { section, fieldId } = action;
            return {
                ...state,
                sections: {
                    ...state.sections,
                    [section]: {
                        ...state.sections[section],
                        fields: {
                            ...state.sections[section].fields,
                            [fieldId]: {
                                ...state.sections[section].fields[fieldId],
                                touched: true,
                            },
                        },
                    },
                },
            };
        }
        case "SET_SUBMIT_ATTEMPTED": {
            return {
                ...state,
                submitAttempted: action.value,
            };
        }

        default:
            return state;
    }
}

export function validateSections(state, fieldListMap, storedKeysMap = {}) {
    for (const section of ["A", "B",]) {
        const fieldList = fieldListMap[section] || state.sections[section]?.fieldList || [];
        const storedKeys = storedKeysMap[section] || [];
        const sectionFields = state.sections[section]?.fields || {};
        const sectionRequirements = state.sections[section]?.requirements || {}; // Section C Validation

        for (const field of fieldList) {
            if (!field.isRequired) continue;

            //  only validate required fields for requirements
            // if (section === "C") {
            //     const reqId = field.sgbpRequirementId;
            //     const hasFile = (sectionRequirements[reqId]?.files || []).length > 0;
            //     if (!hasFile) continue;
            // }

            const fieldId = field.sgbpAdditionalFieldInputId;

            // Find all stored keys that belong to this fieldId (composite or plain)
            const matchingKeys = storedKeys.filter(key =>
                key === String(fieldId) || key.startsWith(`${fieldId}_`)
            );

            if (matchingKeys.length === 0) {
                return false;
            }

            for (const key of matchingKeys) {
                const fieldValue = sectionFields[key]?.value;
                if (fieldValue === undefined || fieldValue === null || fieldValue === "") {
                    return false;
                }
            }
        }
    }
    return true;
}


// ─── Validates that every requirement 
export function validateRequirementFiles(state) {
    for (const section of ["A"]) {
        const reqList = state.sections[section]?.reqList || {};
        const requirements = state.sections[section]?.requirements || {};

        for (const [requirementId, requirementData] of Object.entries(reqList)) {
            const requirementKey = requirementData?.sgbpRequirementId || requirementData?.requirementId || requirementId;
            const files = requirements[requirementKey]?.files || [];
            if (files.length === 0) {
                return {
                    valid: false,
                    message: `Please upload at least one file for all requirements in Section ${section}.`,
                };
            }
        }
    }
    return { valid: true };
}

export function validateSectionBMinimumCriteria(state) {
    const section = "B";
    const minRequired = state.sectionBMinCriteria || 0;
    if (minRequired <= 0) return { valid: true };

    const sectionState = state.sections?.[section];
    const reqList = Object.values(sectionState?.reqList || {});
    const fieldList = sectionState?.fieldList || [];
    const requirements = sectionState?.requirements || {};
    const fields = sectionState?.fields || {};

    const isRequirementComplete = (reqId) => {
        const reqData = requirements[reqId];
        if (!(reqData?.files || []).length) return false;

        const matchedFields = fieldList.filter((f) => f.sgbpRequirementId === reqId);
        return matchedFields.every((f) => {
            if (!f.isRequired) return true;
            const key = buildFieldKey(f.sgbpAdditionalFieldInputId, null); 
            const val = fields[key]?.value;
            return val !== undefined && val !== null && val !== "";
        });
    };

    const completedCount = reqList.filter((item) =>
        isRequirementComplete(item.sgbpRequirementId)
    ).length;

    if (completedCount < minRequired) {
        return {
            valid: false,
            message: `Please complete file upload and required fields for at least ${minRequired} requirement(s) in Section 2.`,
        };
    }
    return { valid: true };
}

export const CaseOfficerServiceProvider = ({ children }) => {

    const [state, dispatch] = useReducer(reducer, initialState);

    return (
        <ServiceContext.Provider value={{ state, dispatch }}>
            {children}
        </ServiceContext.Provider>
    );
};

export const useCaseOfficerServiceContext = () => useContext(ServiceContext);